import React, { useId } from 'react';
import { Loader, Save } from 'lucide-react';
import Sheet from '../../../common/Sheet';
import { getEntryTitle } from '../utils';

export interface EntrySheetProps {
  open: boolean;
  /** Local entry type (vitals, mar, notes, ...) */
  entryType: string | null;
  mode: 'new' | 'edit';
  onCancel: () => void;
  onSave: () => void;
  saving?: boolean;
  /** The form has errors: Save stays disabled */
  invalid?: boolean;
  isReadOnly?: boolean;
  /** Phase 18U U9: when the form text was last kept on this device */
  savedAt?: Date | null;
  /** Shown above the form (the "Resume unsaved … ?" banner) */
  notice?: React.ReactNode;
  /** Phones and field-mode tablets: 48 px footer buttons at any width */
  touch?: boolean;
  children: React.ReactNode;
}

const clock = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * Phase 18U U3: every new entry and every edit opens in a sheet (full screen on a phone, a side
 * panel from 768 px) with Save and Cancel pinned in the footer, so Save never scrolls away.
 * The body is a real <form>: Enter in a single-line field saves (F16), and every
 * button inside the entry forms is type="button" so only Save submits.
 */
const EntrySheet: React.FC<EntrySheetProps> = ({
  open, entryType, mode, onCancel, onSave, saving = false, invalid = false, isReadOnly = false, savedAt = null, notice = null, touch = false, children,
}) => {
  const formId = useId();
  const blocked = saving || invalid || isReadOnly;
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!blocked) onSave();
  };
  return (
    <Sheet
      open={open}
      onClose={() => { if (!saving) onCancel(); }}
      title={getEntryTitle(entryType)}
      subtitle={mode === 'edit' ? 'Edit entry' : 'New entry'}
      width="lg"
      testId="entry-sheet"
      footer={(
        <div className="space-y-2">
          {savedAt && mode === 'new' && !isReadOnly && (
            <p className="text-xs text-gray-600" data-testid="entry-sheet-saved">Draft kept on this device · {clock(savedAt)}</p>
          )}
          <div className="flex gap-2 md:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={`min-h-[48px] flex-1 rounded-lg border border-gray-300 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 md:flex-none md:px-5 ${touch ? '' : 'md:min-h-[40px] md:text-sm'}`}
            >
              Cancel
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                form={formId}
                disabled={blocked}
                aria-disabled={blocked}
                className={`flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 md:flex-none md:px-6 ${touch ? '' : 'md:min-h-[40px] md:text-sm'}`}
              >
                {saving ? <><Loader size={18} className="animate-spin" aria-hidden="true" />Saving…</> : <><Save size={18} aria-hidden="true" />Save</>}
              </button>
            )}
          </div>
        </div>
      )}
    >
      {notice && <div className="mb-3">{notice}</div>}
      <form id={formId} onSubmit={submit} noValidate className="text-left" data-testid="entry-sheet-form">
        {children}
      </form>
    </Sheet>
  );
};

export default EntrySheet;
